﻿import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useClickOutside } from '../hooks/clickOutside';
import ConfirmationModal from './ConfirmationModal';
import { AdditionalProduct, IAdditionalProductButton } from '../EditPage';
import { AddIcon, AdditionalProductIcon, CheckmarkIcon } from './Icons';
import { TranslatedLabels } from '../../../../../shared/types';

type ExtraProductSelectionProps = {
    availableAdditionalProducts: IAdditionalProductButton[];
    onAddExtraProduct: (product: AdditionalProduct) => void;
    labels: TranslatedLabels;
    showAddExtraProductConfirmationModal: boolean;
    onCancelAddExtraProductConfirmationModal: () => void;
    onConfirmAddExtraProductConfirmationModal: () => void;
    onCloseAddExtraProductConfirmationModal: () => void;
};

type AdditionalProductButtonProps = {
    onClick: () => void;
    product: IAdditionalProductButton;
};

function AdditionalProductButton({ onClick, product }: AdditionalProductButtonProps) {
    const [isHover, setIsHover] = useState<boolean>(false);

    function handleClick() {
        onClick();
    }

    function handleOnMouseLeave() {
        setIsHover(false);
    }

    function handleOnMouseEnter() {
        setIsHover(true);
    }

    return (
        <button
            className={`addAdditionalProduct__dropdownAdditionalProductButton${
                product.disabled ? '--selected' : ''
            }`}
            onClick={handleClick}
            aria-label={product.name}
            disabled={product.disabled}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
        >
            {product.disabled ? <CheckmarkIcon /> : <AdditionalProductIcon />}
            {!product.disabled && isHover ? <AddIcon /> : null}
            <span>{product.name}</span>
        </button>
    );
}

function AdditionalProductSelection({
    availableAdditionalProducts,
    onAddExtraProduct,
    labels,
    showAddExtraProductConfirmationModal,
    onCancelAddExtraProductConfirmationModal,
    onConfirmAddExtraProductConfirmationModal,
    onCloseAddExtraProductConfirmationModal
}: ExtraProductSelectionProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedExtraProduct, setSelectedExtraProduct] = useState<AdditionalProduct | null>(
        null
    );
    const addExtraProductDropdownRef = useRef<HTMLButtonElement | null>(null);

    function handleToggleAddExtraProductDropdown() {
        setIsOpen((open) => !open);
    }

    function handleClickOutsideAddExtraProductDropdown() {
        if (isOpen) {
            setIsOpen(false);
        }
    }

    function handleClickAddExtraProduct() {
        onAddExtraProduct(selectedExtraProduct);
    }

    function handleCloseAddExtraProductConfirmationModal() {
        onCloseAddExtraProductConfirmationModal();
    }

    function handleCancelAddExtraProduct() {
        onCancelAddExtraProductConfirmationModal();
    }

    function handleConfirmAddExtraProduct() {
        setSelectedExtraProduct(null);
        onConfirmAddExtraProductConfirmationModal();
    }

    useClickOutside(addExtraProductDropdownRef.current, handleClickOutsideAddExtraProductDropdown, [
        isOpen
    ]);

    return (
        <div>
            <h3 className="addAdditionalProduct__title">{labels.addAdditionalProduct__title}</h3>
            <div className="addAdditionalProduct__contentContainer">
                <div className="addAdditionalProduct__dropdownButtonContainer">
                    <button
                        className="addAdditionalProduct__dropdownButton"
                        ref={addExtraProductDropdownRef}
                        onClick={handleToggleAddExtraProductDropdown}
                        aria-label={
                            selectedExtraProduct
                                ? selectedExtraProduct.name
                                : labels.addAdditionalProduct__placeholder
                        }
                    >
                        <span>
                            {selectedExtraProduct
                                ? selectedExtraProduct.name
                                : labels.addAdditionalProduct__placeholder}
                        </span>
                        <i className="material-icons addAdditionalProduct__dropdownButtonIcon">
                            {isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </i>
                    </button>
                    {isOpen ? (
                        <div className="addAdditionalProduct__dropdownContentContainer">
                            {availableAdditionalProducts.map((additionalProduct) => {
                                function handleClickExtraProduct() {
                                    setSelectedExtraProduct(additionalProduct);
                                }

                                return (
                                    <AdditionalProductButton
                                        key={additionalProduct.id}
                                        product={additionalProduct}
                                        onClick={handleClickExtraProduct}
                                    />
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                <button
                    className={`addAdditionalProduct__addAdditionalProductButton${
                        selectedExtraProduct === null ? '--disabled' : ''
                    }`}
                    onClick={handleClickAddExtraProduct}
                    disabled={selectedExtraProduct === null}
                >
                    <i className="material-icons addAdditionalProduct__addAdditionalProductButtonIcon">
                        add
                    </i>
                    <span>{labels.addAdditionalProduct__addButtonLabel}</span>
                </button>
            </div>
            <ConfirmationModal
                isOpen={showAddExtraProductConfirmationModal}
                onClose={handleCloseAddExtraProductConfirmationModal}
                title={labels.addAdditionalProduct__confirmationModal__title}
                textContent={
                    <>
                        <p>{labels.addAdditionalProduct__confirmationModal__text}</p>
                        {selectedExtraProduct ? <p>({selectedExtraProduct.name})</p> : null}
                    </>
                }
            >
                <Button variant="secondary" onClick={handleCancelAddExtraProduct}>
                    {labels.addAdditionalProduct__confirmationModal__cancelButtonLabel}
                </Button>
                <Button
                    className="editPhotoOrder__confirmationModal__confirmButton"
                    variant="primary"
                    onClick={handleConfirmAddExtraProduct}
                >
                    {labels.addAdditionalProduct__confirmationModal__confirmButtonLabel}
                </Button>
            </ConfirmationModal>
        </div>
    );
}

export default AdditionalProductSelection;
